import { useLatest } from 'ahooks';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Scrollbars } from '@meitu/saas-ui';
import { IWatermarkLayerProps, LayerType } from '@meitu/saas-editor-core/es/version-types';
import { CollapseType } from '@meitu/saas-editor-ui/es/types';
import { useDisableAppData } from '@/hooks';
import { isEditTextArt, isEditTextMark } from '@/hooks/fetch-template/editor-mode';
import { editor } from '@/instance';
import { useCollapseModel } from '@/store/collapse-models/collapse-model';
import { useEditorModel } from '@/store/models/editor-model';
import { panelConf } from './config';
import { TextArtPanelKey } from './views/text-art-panel/text-art-panel';
import { TextMarkPanelKey } from './views/text-mark-panel/text-mark-panel';
import './index.less';

const Panel = () => {
    const actualType = useCollapseModel((state) => state.actualType);
    const isBackgroundOperation = useEditorModel((state) => state.isBackgroundOperation);
    const disableApp = useDisableAppData();
    const actualTypeRef = useLatest(actualType);
    const isBackgroundOperationRef = useLatest(isBackgroundOperation);
    const [layerType, setLayerType] = useState<keyof typeof panelConf | null>(null);
    // 获取面板类型
    const getPanelType = (): keyof typeof panelConf => {
        // 背景操作
        if (isBackgroundOperationRef.current) {
            return LayerType.BACKGROUND_LAYER;
        }
        // 当前选中元素
        const layer = editor.services.frame.getActiveLayer();
        if (layer) {
            return layer.layerType;
        }
        // 全局水印菜单
        if (actualTypeRef.current === CollapseType.WATERMARK_MENU) {
            const layers = editor.services.frame.getLayers(editor.services.frame.getActiveFrameId());
            for (const layer of layers) {
                if (layer.layerType === LayerType.WATERMARK && (layer.data as IWatermarkLayerProps).isFullScreen) {
                    return LayerType.WATERMARK;
                }
            }
        }

        // 默认返回背景面板
        return isEditTextMark() ? TextMarkPanelKey : isEditTextArt() ? TextArtPanelKey : LayerType.BACKGROUND_LAYER;
    };

    // ATTENTION: 打开左侧栏打开元素菜单里面的背景模块时，右侧面板需要切换为背景面板
    useEffect(() => {
        setLayerType(getPanelType());
    }, [actualType, isBackgroundOperation]);

    useEffect(() => {
        const handler = editor.services.frame.onActiveLayerDataUpdate(() => {
            setLayerType(getPanelType());
        }, 9);
        return () => editor.off(handler);
    }, []);

    useEffect(() => {
        const handler = editor.watermarkLayer.onFullScreenChanged(() => {
            setLayerType(getPanelType());
        });
        return () => editor.off(handler);
    }, []);

    return (
        <div className={classNames('p-panel', disableApp)}>
            <Scrollbars autoHide style={{ width: '260px', height: '100%' }}>
                {layerType ? panelConf[layerType] : null}
            </Scrollbars>
        </div>
    );
};

export default React.memo(Panel);
